import { FILTER_KEYS, FILTER_VALUES, QUERY_BEHAVIOR } from '../../constants';
import { minUtrValue, maxUtrValue } from '../../../../../utils/constants';

const {
  TYPE,
  EVENT_STATE,
  DATE_RANGE,
  START_DATE,
  END_DATE,
  UTR_MIN,
  UTR_MAX,
  UTR_TYPE,
  UTR_TEAM_TYPE,
  SPORT_TYPES,
  TOURS,
  EVENT_TYPES,
  EVENT_UTR_TYPES,
  EVENT_TEAM_TYPES,
  GENDERS,
  BALL_TYPES,
  LOCATION,
  LAT,
  LNG,
  DISTANCE,
  LOCATION_INPUT_VALUE,
  QUERY,
} = FILTER_KEYS;
const { EVENTS, OPEN_REGISTRATION, VERIFIED, SINGLES, DEFAULT_DISTANCE } = FILTER_VALUES;
/**
 * Determine initial filters from URL parameters and user preferences
 */
export const determineInitialFilters = (parsed, auth) => {
  const { user, feedPreferences } = auth || {};
  const hasQueryParam = Object.keys(parsed).length > 0 && parsed?.query;

  // Base filters for events
  const baseFilters = {
    [TYPE]: EVENTS,
    [UTR_MIN]: minUtrValue,
    [UTR_MAX]: maxUtrValue,
    [UTR_TYPE]: VERIFIED,
    [UTR_TEAM_TYPE]: SINGLES,
    [TOURS]: {},
    [EVENT_TYPES]: {},
    [EVENT_UTR_TYPES]: {},
    [EVENT_TEAM_TYPES]: {},
    [GENDERS]: {},
    [BALL_TYPES]: {},
  };

  // Determine sport types
  let sportTypes = { tennis: true, pickleball: true };

  if (user && feedPreferences) {
    sportTypes = {
      tennis: feedPreferences?.showTennisContent ?? true,
      pickleball: feedPreferences?.showPickleballContent ?? true,
    };
  }

  baseFilters[SPORT_TYPES] = sportTypes;

  // Set default eventState and location for new searches (no query params)
  if (!hasQueryParam) {
    baseFilters[EVENT_STATE] = OPEN_REGISTRATION;

    // Only use user's location if the URL does NOT already specify any location params
    const hasLocationParamsInUrl = Boolean(
      parsed?.[LOCATION] ||
      parsed?.[LAT] ||
      parsed?.[LNG] ||
      parsed?.[DISTANCE] ||
      parsed?.locationCountryName ||
      parsed?.locationStateName,
    );

    if (!hasLocationParamsInUrl && user?.location?.display) {
      const userLocationLatLng = user.location.latLng || [];
      baseFilters[LOCATION] = user.location.display;
      baseFilters[DISTANCE] = DEFAULT_DISTANCE;
      baseFilters[LAT] = userLocationLatLng[0] || null;
      baseFilters[LNG] = userLocationLatLng[1] || null;
    }
  } else {
    // When we have query parameters (from global search), add essential UTR parameters
    baseFilters[UTR_MIN] = minUtrValue;
    baseFilters[UTR_MAX] = maxUtrValue;
    baseFilters[UTR_TYPE] = VERIFIED;

    // Clear fields for query-based searches (no defaults)
    QUERY_BEHAVIOR.FIELDS_CLEARED_BY_QUERY.forEach(field => {
      baseFilters[field] = null;
    });
  }

  // Parse URL parameters into filters
  Object.keys(parsed).forEach(key => {
    if (key === QUERY) {
      // Query is handled separately
      return;
    }

    if (key === LOCATION) {
      // Map URL 'location' to both the filter display value and the input value
      baseFilters[LOCATION] = parsed[key];
      baseFilters[LOCATION_INPUT_VALUE] = parsed[key];
    } else if (key === TOURS) {
      const tours = parsed[key].split(',');
      tours.forEach(tour => {
        baseFilters[TOURS][decodeURI(tour)] = true;
      });
    } else if (key === EVENT_TYPES) {
      const eventTypes = parsed[key].split(',');
      eventTypes.forEach(eventType => {
        baseFilters[EVENT_TYPES][decodeURI(eventType)] = true;
      });
    } else if (key === EVENT_UTR_TYPES) {
      const eventUtrTypes = parsed[key].split(',');
      eventUtrTypes.forEach(eventUtrType => {
        baseFilters[EVENT_UTR_TYPES][decodeURI(eventUtrType)] = true;
      });
    } else if (key === GENDERS) {
      const genders = parsed[key].split(',');
      genders.forEach(gender => {
        baseFilters[GENDERS][decodeURI(gender)] = true;
      });
    } else if (key === EVENT_TEAM_TYPES) {
      const eventTeamTypes = parsed[key].split(',');
      eventTeamTypes.forEach(eventTeamType => {
        baseFilters[EVENT_TEAM_TYPES][decodeURI(eventTeamType)] = true;
      });
    } else if (key === BALL_TYPES) {
      const ballTypes = parsed[key].split(',');
      ballTypes.forEach(ballType => {
        baseFilters[BALL_TYPES][decodeURI(ballType)] = true;
      });
    } else if (key === SPORT_TYPES) {
      const sportTypesFromUrl = {};
      parsed[key].split(',').forEach(sportType => {
        sportTypesFromUrl[decodeURI(sportType)] = true;
      });
      baseFilters[SPORT_TYPES] = sportTypesFromUrl;
    } else if (key === UTR_MIN || key === UTR_MAX) {
      baseFilters[key] = parseInt(parsed[key], 10);
    } else {
      // For Event Search V2, don't override eventState if there's a query parameter
      if (key === EVENT_STATE && parsed?.query) {
        return;
      }
      baseFilters[key] = parsed[key];
    }
  });

  // Handle mutually exclusive filters - eventState (Open Registration) takes priority over dateRange/custom
  // When both are present (e.g., from browser back button), Open Registration should be the default
  if (baseFilters[EVENT_STATE]) {
    // eventState takes priority - clear dateRange and custom date fields
    baseFilters[DATE_RANGE] = null;
    baseFilters[START_DATE] = null;
    baseFilters[END_DATE] = null;
  } else if (baseFilters[DATE_RANGE]) {
    // If dateRange is set, clear eventState and any custom date fields
    baseFilters[EVENT_STATE] = null;
    baseFilters[START_DATE] = null;
    baseFilters[END_DATE] = null;
  } else if (baseFilters[START_DATE] || baseFilters[END_DATE]) {
    // If custom dates are set, clear eventState and dateRange
    baseFilters[EVENT_STATE] = null;
    baseFilters[DATE_RANGE] = null;
  }

  return baseFilters;
};
